/**
 * Suggest compatible device models from a part name.
 * Matches against a known catalog (longest token-boundary match wins).
 * Does not talk to the database — callers cache the catalog.
 */

export type DeviceSuggestConfidence = 'high' | 'medium' | 'low';

export type CatalogModel = {
  modelId: string;
  modelName: string;
  makeId: string;
  makeName: string;
};

export type CatalogAlias = {
  alias: string;
  modelId: string;
};

export type SuggestCatalog = {
  models: CatalogModel[];
  aliases: CatalogAlias[];
};

export type DeviceSuggestion = {
  modelId: string;
  modelName: string;
  makeId: string;
  makeName: string;
  confidence: DeviceSuggestConfidence;
  matchedText: string;
};

export type SuggestOptions = {
  /** When the Device column is blank but Manufacturer is set. */
  restrictMakeName?: string;
};

const CONFIDENCE_RANK: Record<DeviceSuggestConfidence, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

/** Lowercase, collapse whitespace, strip punctuation except + and ". */
export function normalizePartText(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[“”]/g, '"')
    .replace(/[^a-z0-9+"\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isTokenChar(ch: string | undefined): boolean {
  return ch != null && /[a-z0-9+"]/.test(ch);
}

function occursAtBoundary(haystack: string, needle: string): Array<{ start: number; end: number }> {
  const spans: Array<{ start: number; end: number }> = [];
  if (needle.length < 2) return spans;
  let from = 0;
  while (from <= haystack.length - needle.length) {
    const index = haystack.indexOf(needle, from);
    if (index < 0) break;
    const end = index + needle.length;
    const beforeOk = index === 0 || !isTokenChar(haystack[index - 1]);
    const afterOk = end === haystack.length || !isTokenChar(haystack[end]);
    if (beforeOk && afterOk) spans.push({ start: index, end });
    from = index + 1;
  }
  return spans;
}

/**
 * Parenthetical slash lists of model codes become one search string per code.
 * "A9+ (X210 / X215 / X218)" expands. "A03s (A037 / 2021)" does not — 2021 is a year.
 */
export function expandParenModelCodes(raw: string): string[] {
  const out: string[] = [];
  const re = /\(([^()]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw))) {
    const inner = match[1] ?? '';
    if (!inner.includes('/')) continue;
    const parts = inner
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 2) continue;
    const allCodes = parts.every((part) => /[A-Za-z]/.test(part) && !/^\d{4}$/.test(part));
    if (!allCodes) continue;
    const before = raw.slice(0, match.index);
    const after = raw.slice(match.index + match[0].length);
    for (const code of parts) {
      out.push(`${before}(${code})${after}`.replace(/\s+/g, ' ').trim());
    }
  }
  return out;
}

function searchTexts(raw: string): string[] {
  const texts = new Set<string>();
  const trimmed = raw.trim();
  if (trimmed) texts.add(trimmed);
  for (const expanded of expandParenModelCodes(trimmed)) texts.add(expanded);
  return [...texts];
}

type Span = {
  start: number;
  end: number;
  model: CatalogModel;
  matchedText: string;
  via: 'name' | 'alias' | 'partial';
};

function suppressContained(spans: Span[]): Span[] {
  const sorted = [...spans].sort(
    (a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start,
  );
  const kept: Span[] = [];
  for (const span of sorted) {
    const insideLonger = kept.some(
      (other) =>
        span.start >= other.start &&
        span.end <= other.end &&
        span.end - span.start < other.end - other.start,
    );
    if (!insideLonger) kept.push(span);
  }
  return kept;
}

function statedMakeIds(haystack: string, models: CatalogModel[]): Set<string> {
  const ids = new Set<string>();
  const byName = new Map<string, string>();
  for (const model of models) {
    const key = normalizePartText(model.makeName);
    if (key.length < 2 || byName.has(key)) continue;
    byName.set(key, model.makeId);
  }
  for (const [key, id] of byName) {
    if (occursAtBoundary(haystack, key).length > 0) ids.add(id);
  }
  return ids;
}

export function suggestCompatibleDevices(
  partName: string,
  catalog: SuggestCatalog,
  opts?: SuggestOptions,
): DeviceSuggestion[] {
  const models = catalog.models ?? [];
  const aliases = catalog.aliases ?? [];
  if (!partName.trim() || models.length === 0) return [];

  const byId = new Map(models.map((model) => [model.modelId, model]));
  const texts = searchTexts(partName);
  const primary = normalizePartText(partName);
  const candidates: Span[] = [];

  for (const text of texts) {
    const haystack = normalizePartText(text);
    if (!haystack) continue;
    const spans: Span[] = [];
    for (const model of models) {
      const needle = normalizePartText(model.modelName);
      for (const span of occursAtBoundary(haystack, needle)) {
        spans.push({
          ...span,
          model,
          matchedText: model.modelName,
          via: 'name',
        });
      }
    }
    for (const alias of aliases) {
      const model = byId.get(alias.modelId);
      if (!model) continue;
      const needle = normalizePartText(alias.alias);
      for (const span of occursAtBoundary(haystack, needle)) {
        spans.push({
          ...span,
          model,
          matchedText: alias.alias.trim(),
          via: 'alias',
        });
      }
    }
    candidates.push(...suppressContained(spans));
  }

  const stated = statedMakeIds(primary, models);
  let filtered = candidates;
  if (stated.size > 0) {
    filtered = filtered.filter((span) => stated.has(span.model.makeId));
  }

  const restrict = opts?.restrictMakeName ? normalizePartText(opts.restrictMakeName) : '';
  if (restrict) {
    const allowed = new Set(
      models.filter((model) => normalizePartText(model.makeName) === restrict).map((model) => model.makeId),
    );
    if (allowed.size > 0) {
      filtered = filtered.filter((span) => allowed.has(span.model.makeId));
    }
  }

  const makesByModelName = new Map<string, Set<string>>();
  for (const model of models) {
    const key = normalizePartText(model.modelName);
    const set = makesByModelName.get(key) ?? new Set<string>();
    set.add(model.makeId);
    makesByModelName.set(key, set);
  }

  const best = new Map<string, DeviceSuggestion>();
  const consider = (suggestion: DeviceSuggestion) => {
    const prev = best.get(suggestion.modelId);
    if (!prev || CONFIDENCE_RANK[suggestion.confidence] > CONFIDENCE_RANK[prev.confidence]) {
      best.set(suggestion.modelId, suggestion);
    }
  };

  for (const span of filtered) {
    const nameKey = normalizePartText(span.model.modelName);
    const makeCount = makesByModelName.get(nameKey)?.size ?? 1;
    let confidence: DeviceSuggestConfidence = 'high';
    if (span.via === 'alias') confidence = 'medium';
    else if (stated.size === 0 && makeCount > 1) confidence = 'medium';
    consider({
      modelId: span.model.modelId,
      modelName: span.model.modelName,
      makeId: span.model.makeId,
      makeName: span.model.makeName,
      confidence,
      matchedText: span.matchedText,
    });
  }

  const matchedIds = new Set(best.keys());
  for (const model of models) {
    if (matchedIds.has(model.modelId)) continue;
    if (stated.size > 0 && !stated.has(model.makeId)) continue;
    if (restrict) {
      const allowedMake = normalizePartText(model.makeName) === restrict;
      if (!allowedMake && models.some((row) => normalizePartText(row.makeName) === restrict)) continue;
    }
    const tokens = normalizePartText(model.modelName).split(' ').filter((token) => {
      return token.length >= 4 && /[a-z]/.test(token) && /\d/.test(token);
    });
    for (const token of tokens) {
      if (occursAtBoundary(primary, token).length === 0) continue;
      const owners = models.filter((row) =>
        normalizePartText(row.modelName)
          .split(' ')
          .includes(token),
      );
      if (owners.length > 2) continue;
      consider({
        modelId: model.modelId,
        modelName: model.modelName,
        makeId: model.makeId,
        makeName: model.makeName,
        confidence: 'low',
        matchedText: token,
      });
      break;
    }
  }

  return [...best.values()].sort((a, b) => {
    const rank = CONFIDENCE_RANK[b.confidence] - CONFIDENCE_RANK[a.confidence];
    if (rank !== 0) return rank;
    return a.modelName.localeCompare(b.modelName);
  });
}

const ALIAS_STOP = new Set([
  'for',
  'and',
  'the',
  'with',
  'oled',
  'lcd',
  'tft',
  'incell',
  'screen',
  'assembly',
  'digitizer',
  'charging',
  'port',
  'board',
  'flex',
  'cable',
  'battery',
  'glass',
  'plus',
  'pro',
  'max',
  'oem',
  'new',
  'black',
  'white',
  'frame',
  'housing',
  'camera',
  'speaker',
  'button',
  'part',
]);

/** A shorthand token in the part name that is not the model name itself. */
export function aliasCandidateFromName(
  partName: string,
  modelName: string,
  suggestions: DeviceSuggestion[],
): string | null {
  const modelNorm = normalizePartText(modelName);
  const suggestedText = new Set(suggestions.map((row) => normalizePartText(row.matchedText)));
  const tokens = partName.split(/[^A-Za-z0-9+]+/).filter(Boolean);
  for (const token of tokens) {
    const normalized = normalizePartText(token);
    if (normalized.length < 3 || normalized.length > 40) continue;
    if (ALIAS_STOP.has(normalized)) continue;
    if (!/\d/.test(normalized)) continue;
    if (modelNorm === normalized || modelNorm.includes(normalized)) continue;
    if (suggestedText.has(normalized)) continue;
    return token;
  }
  return null;
}
